import { useMemo } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveImage, resolveLinkProps, resolveVideo } from '../utils';
import SectionDivider from './SectionDivider';

export default function TextVideoMaskSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'gradient';

    const video = resolveVideo(content, 'video', mediaFolders);
    const bgImage = resolveImage(content, 'image', mediaFolders) || content.image_url;
    const displayText = content.text || 'CRAFTED';
    const fontSize = config.font_size || '10rem';

    const cls = `relative overflow-hidden ${s.radiusClass} ${s.shadowClass}`;
    const sectionBg = config.bg_color || '#0a0a0a';

    // Build the text style per variant
    const textStyle = useMemo(() => {
        const base = {
            fontSize: `clamp(3rem, 12vw, ${fontSize})`,
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: '-0.02em',
            lineHeight: 1,
            display: 'block',
        };

        if (variant === 'gradient') {
            return {
                ...base,
                backgroundImage: `linear-gradient(135deg, ${s.accent}, ${secondaryColor || '#f59e0b'}, ${hexToRgba(s.accent, 0.8)}, ${primaryColor || s.accent})`,
                backgroundSize: '300% 300%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
            };
        }

        if (variant === 'image') {
            if (bgImage) {
                return {
                    ...base,
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                };
            }
            // Image not set yet — show accent gradient as placeholder
            return {
                ...base,
                backgroundImage: `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.5)})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
            };
        }

        if (variant === 'video') {
            return {
                ...base,
                color: '#ffffff',
                textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            };
        }

        // fallback — gradient
        return {
            ...base,
            backgroundImage: `linear-gradient(135deg, ${s.accent} 0%, ${secondaryColor || '#f59e0b'} 40%, ${hexToRgba(s.accent, 0.9)} 70%, #ffffff 100%)`,
            backgroundSize: '300% 300%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
        };
    }, [variant, s.accent, secondaryColor, primaryColor, bgImage, fontSize]);

    return (
        <section className={cls} style={{ backgroundColor: sectionBg, paddingTop: s.padClass.includes('py-') ? undefined : '5rem', paddingBottom: s.padClass.includes('py-') ? undefined : '5rem' }}>
            <div className={`${s.padClass.replace(/bg-\S+/g, '')} relative`}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>

                {/* Background: video (for 'video' variant) */}
                {variant === 'video' && video && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                        {video.type === 'file' ? (
                            <video src={video.src} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
                        ) : video.embed ? (
                            <iframe
                                src={`${video.embed}?autoplay=1&mute=1&loop=1${video.type === 'youtube' ? `&playlist=${video.id}&controls=0&playsinline=1` : '&background=1'}`}
                                className="absolute w-[200%] h-[200%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                                frameBorder="0"
                                allow="autoplay; fullscreen"
                                title="background video"
                            />
                        ) : null}
                        {/* Subtle overlay for text readability */}
                        <div className="absolute inset-0" style={{ backgroundColor: hexToRgba(sectionBg, 0.2) }} />
                    </div>
                )}
                {/* Placeholder when video variant selected but no video set */}
                {variant === 'video' && !video && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.15)}, ${hexToRgba(secondaryColor || s.accent, 0.1)})` }}>
                            <svg className="w-16 h-16 opacity-15 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </div>
                    </div>
                )}

                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    {/* Overline tag */}
                    {content.tag && (
                        <p className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: hexToRgba('#ffffff', 0.45) }}>
                            {content.tag}
                        </p>
                    )}

                    {/* The masked / clipped text */}
                    <span
                        className={variant === 'gradient' ? 'tvm-grad tvm-pulse' : ''}
                        style={textStyle}
                        aria-label={displayText}
                    >
                        {displayText}
                    </span>

                    {/* Subtitle */}
                    {content.subtitle && (
                        <p className="mt-8 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{ color: hexToRgba('#ffffff', 0.7) }}>
                            {content.subtitle}
                        </p>
                    )}

                    {/* CTA */}
                    {content.cta_text && content.cta_link && (
                        <div className="mt-10">
                            <a
                                {...resolveLinkProps(content.cta_link)}
                                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                                style={{
                                    backgroundColor: s.accent,
                                    color: '#ffffff',
                                    boxShadow: `0 4px 20px ${hexToRgba(s.accent, 0.4)}`,
                                }}
                            >
                                {content.cta_text}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </a>
                        </div>
                    )}
                </div>

                <SectionDivider type={config.divider_bottom} color="#0a0a0a" position="bottom"  config={config}/>
            </div>
            {variant === 'gradient' && (
                <style>{`@keyframes tvmGrad{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}@keyframes tvmPulse{0%,100%{opacity:1}50%{opacity:.85}}.tvm-grad{background-size:300% 300%;animation:tvmGrad 6s ease infinite;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent}.tvm-pulse{animation:tvmPulse 3s ease-in-out infinite}`}</style>
            )}
        </section>
    );
}
